import { ApiResponse } from 'apisauce';

import { PurchasesPayload } from '@/Modules/PurchaseOrdersModule/shared/types/search-purchases.type';
import { PurchaseOrderReportStats } from '@/Modules/ReportsModule/PurchaseOrdersReport/shared/store/purchase-orders-reports.store';
import debugConfig from '@/shared/config/debug.config';
import api from '@/shared/config/http.config';
import { ErrorInfo } from '@/shared/interfaces/error-info.interface';
import { ResponseModel } from '@/shared/interfaces/response-model.interface';
import { PurchaseDocumentsDataModel, PurchasesModel } from '@/shared/models/purchases.model';
import purchasesFixtureService from '@/shared/services/fixtures/purchases/purchases-api.service';
import { PurchaseOrderSearchQuery } from '@/shared/ui-models/purchase.ui-model';

const apis = {
  getPurchaseById: (id: string): Promise<ApiResponse<ResponseModel<PurchasesModel>, ErrorInfo>> =>
    api.get(`/purchase-orders/${id}`),

  searchPurchases: (
    searchPurchases: PurchasesPayload
  ): Promise<ApiResponse<ResponseModel<PurchasesModel[]>, ErrorInfo>> =>
    api.post('/purchase-orders/search', searchPurchases),

  createPurchase: (purchase: PurchasesModel): Promise<ApiResponse<{ id: string }, ErrorInfo>> =>
    api.post('/purchase-orders', purchase),

  updatePurchase: (
    purchase: PurchasesModel
  ): Promise<ApiResponse<ResponseModel<PurchasesModel>, ErrorInfo>> =>
    api.put(`/purchase-orders`, purchase),

  // ------------------------------- purchase order documents endpoints -------------------------------
  createPurchaseDocuments: (
    purchaseDocuments: FormData
  ): Promise<ApiResponse<{ message: string }, ErrorInfo>> =>
    api.post('/purchase-order-upload-asset', purchaseDocuments, {
      headers: { Accept: 'multipart/form-data' }
    }),

  getPurchaseDocumentsById: (
    purchaseOrderId: string
  ): Promise<ApiResponse<ResponseModel<PurchaseDocumentsDataModel>, ErrorInfo>> =>
    api.get(`/purchase-order-upload-asset/${purchaseOrderId}`),

  deletePurchaseDocuments: (
    key: string
  ): Promise<ApiResponse<ResponseModel<PurchaseDocumentsDataModel>, ErrorInfo>> =>
    api.delete('/purchase-order-upload-asset', {}, { data: { key } }),

  getPurchaseSignedUrl: (key: string): Promise<ApiResponse<{ url: string }, ErrorInfo>> =>
    api.post('/purchase-order-upload-asset/get-signed-url', {}, { data: { key } }),

  // ------------------------------- purchase order generate documents endpoints -------------------------------
  generateTrustReceipt: (purchaseOrderId: string): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.get(
      `/purchase-orders/generate/trust-receipt/${purchaseOrderId}`,
      {},
      { responseType: 'blob' }
    ),

  generateReceiptAcknowledgment: (purchaseOrderId: string): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.get(
      `/purchase-orders/generate/receipt-acknowledgment/${purchaseOrderId}`,
      {},
      { responseType: 'blob' }
    ),

  generateReceiptAcknowledgmentAndLicensingCommitment: (
    purchaseOrderId: string
  ): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.get(
      `/purchase-orders/generate/receipt-acknowledgment-and-licensing-commitment/${purchaseOrderId}`,
      {},
      { responseType: 'blob' }
    ),

  generatePurchaseOrder: (purchaseOrderId: string): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.get(
      `/purchase-orders/generate/purchase-order/${purchaseOrderId}`,
      {},
      { responseType: 'blob' }
    ),

  getPurchaseOrdersReportStats: (
    payload: PurchaseOrderSearchQuery
  ): Promise<ApiResponse<ResponseModel<PurchaseOrderReportStats>, ErrorInfo>> =>
    api.post('/purchase-orders/report/profit', payload),

  exportPurchaseOrderProfitReport: (
    exportPurchaseOrderProfitReport: Pick<PurchasesPayload, 'query' | 'sort'>
  ): Promise<ApiResponse<Blob, ErrorInfo>> =>
    api.post('/purchase-orders/export/report/profit', exportPurchaseOrderProfitReport, {
      responseType: 'blob'
    })
};

// TODO: Enhance the way of mocking APIs
export const purchasesApi = debugConfig.useFixtures ? purchasesFixtureService : apis;
